import { FunctionComponent, useState } from 'react';
import useCollections, { CollectionState } from 'model/collection/CollectionStore';
import Select from '@mui/material/Select';
import {MenuItem, Box, Button, } from '@mui/material';
import { TextBox } from 'components/common/TextBox';
import { Label } from 'components/common/Label';
import { FormattedInput } from 'components/common/CurrencyTextField'
import { validateV2 } from "model/collection/CollectionValidation"
import { Typography, Stack } from '@mui/material';
import PaymentMethodComponent from 'components/collection/PaymentMethod'
import { KeyboardArrowRight } from '@mui/icons-material';
import {PAYPAL, CASH_APP, ZELLE, VENMO} from 'assets/Icons'

import { PaymentMethod } from 'model/collection/CollectionModels';
import { log } from 'utils/Common';

export interface CardProps {
  back: () => void;
  backText: string;
  next: () => void;
  nextText: string;
  isNew: boolean;
  canCancel: boolean;
}

const PAYMENT_OPTIONS = [
  {id: "paypal", displayName: "PayPal", icon: PAYPAL(24)},
  {id: "venmo", displayName: "Venmo", icon: VENMO(24)},
  {id: "cashapp", displayName: "CashApp", icon: CASH_APP(24)},
  // {id: "zelle", displayName: "Zelle", icon: ZELLE(24)},
]

const FIELDS = new Set(["/recipient", "/amount", "/paymentMethods"])

export const PaymentCard: FunctionComponent<CardProps> = ({back, backText, next, nextText, isNew, canCancel}) => {
  // Store References
  const formData = useCollections((state: CollectionState) => state.collection);
  const updateCollection = useCollections((state: CollectionState) => state.updateCollection);

  const [violations, setViolations] = useState(new Map());
  const [visited] =  useState(isNew?new Set<string>():FIELDS);

  const [paymentOptions, setPaymentOptions] = useState(isNew?PAYMENT_OPTIONS:PAYMENT_OPTIONS.filter((o: any) => !formData.paymentMethods.some(p => p.service === o.id)))
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("")
  
  const handleChange = (el: any) => {
    const { name, value } = el.target;
    updateCollection(name, value);
  };

  const handleNumberChange = (el: any): any => {
    const { name, value } = el.target;
    updateCollection(name, Number(value));
  };

  const handleBlur = (el: any) => {
    visited.add(`/${el.target.name}`);
    log("handleBlur: visited")
    log(visited)
    let errors = validateV2(formData, visited);
    setViolations(errors)
  }

  const handleNext = async () => {
    log("handleNext: formData");
    log(formData);
    // TODO: This is quite interesting
    FIELDS.forEach(s => visited.add(s))
    formData.paymentMethods.forEach((p, i) => visited.add(`/paymentMethods/${i}/serviceUser`))

    log("handleNext: visited")
    log(visited)
    let errors = validateV2(formData, visited);
    
    setViolations(errors);
    if (errors.size > 0) {
      return;
    }
    log("errors");
    log(errors);
    
    next();
  }

  const handleCollectionBlur = (el: any, serviceName: string, index: number) => {
    visited.add(`/paymentMethods/${index}`);
    visited.add(`/paymentMethods/${index}/${el.target.name}`);
    log("handleCollectionBlur: visited")
    log(visited)
    let errors = validateV2(formData, visited);
    setViolations(errors)
  }
  
  const handleCollectionChange = (el: any, serviceName: string, index: number) => {
    const updatedItems = formData.paymentMethods.map(item =>
      item.service === serviceName ? { ...item, [el.target.name]: el.target.value } : item
    );
    
    updateCollection("paymentMethods", updatedItems)
  };

  const addPaymentMethod = (e: any) => {
    if (!formData.paymentMethods) {
      formData.paymentMethods = []
    }
    formData.paymentMethods.push(new PaymentMethod(e.target.value))
    updateCollection("paymentMethods", formData.paymentMethods)
    setPaymentOptions(prevPaymentOptions => prevPaymentOptions.filter(item => item.id !== e.target.value));
  }

  const removePaymentMethod = (serviceName: string) => {
    if (!formData.paymentMethods) {
      formData.paymentMethods = []
    }
    
    updateCollection("paymentMethods", formData.paymentMethods.filter((po) => po.service !== serviceName))
    setPaymentOptions(prevPaymentOptions => 
      [...prevPaymentOptions, PAYMENT_OPTIONS.filter((po) => po.id === serviceName)[0]])
    visited.delete(`/paymentMethods/${formData.paymentMethods.findIndex((po) => po.service === serviceName)}/serviceUser`);
  }

  return (<>
    <Typography variant='h5'>Payment Information</Typography>
    <Typography variant='body1' color="secondary">
      Enter the payer's email address and preferred payment methods.
    </Typography>
    <br/>
    <TextBox
      focused={true}
      name="recipient"
      label="Payer Email Address"
      value={formData.recipient}
      onChange={handleChange}
      onBlur={handleBlur}
      error={violations?.get("/recipient")}
      helpText="The recipient email address" 
      maxLength={250}/>

    <FormattedInput
      name="amount"
      label="Amount to Collect"
      value={formData.amount}
      onChange={handleNumberChange}
      onBlur={handleBlur}
      error={violations?.get("/amount")}
      helpText="The amount" />

    <div style={{display: paymentOptions.length === 0? 'none':'block'}}>
    <Label label="Select Payment Methods" />
    <Select required
      value={selectedPaymentMethod}
      sx={{ mx: 0, mt: "2px", mb: "14px" }}
      fullWidth
      variant="outlined"
      name="paymentMethods"
      error={violations?.has("/paymentMethods")}
      displayEmpty
      renderValue={(selected: any) => {
        if (selected === "") {
          if (formData.paymentMethods.length > 0) {
            return <Typography color="secondary">More payment options</Typography>;
          } else {
            return <Typography color="secondary">Add a payment method</Typography>;
          }
        }
        return selected;
      }}
      onChange={(e: any) => {
        // handleChange(e)
        addPaymentMethod(e);
        handleBlur(e);
      }}>
        {paymentOptions.map((paymentOption, index) => 
          <MenuItem key={index} value={paymentOption.id} sx={{textAlign: "center"}}>
            <Stack spacing={1} direction={'row'}>
              <span>{paymentOption.icon}</span>
              <span>{paymentOption.displayName}</span>
            </Stack>
          </MenuItem>
        )}
    </Select>
    {violations?.get("/paymentMethods") && (
      <div style={{ marginTop: "-12px", textAlign: "right" }}>
        <Typography variant="caption" color="error">
          {violations?.get("/paymentMethods")}
        </Typography>
      </div>
    )}
    </div>
    {/* https://venmo.com/atzanis?txn=pay&audience=friends&amount=34 */}
    {/* https://www.paypal.com/paypalme/aristzanis/228 */}
    {/* https://cash.app/$yourcashtag */}
    
    {/* Base64-encoded https://enroll.zellepay.com/qr-codes?data=eyJ0b2tlbiI6ImF0emFuaXNAeWFob28uY29tIiwibmFtZSI6IkFyaXN0ZWlkaXMifQ== */}
    {/* {
      "token": "atzanis@yahoo.com",
      "name": "Aristeidis"
    }*/}
    {!formData.paymentMethods?<></>:    
      formData.paymentMethods.map((method, index) => 
      <PaymentMethodComponent key={method.service}
                              method={method}
                              onBlur={handleCollectionBlur}
                              onChange={handleCollectionChange}
                              violations={violations}
                              index={index}
                              onDelete={(serviceName: string) => removePaymentMethod(serviceName)}/>)
    }

    <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
      <Button color="inherit" onClick={back} sx={{ mr: 1 }} disabled={!canCancel}>
        {backText}
      </Button>
      <Box sx={{ flex: '1 1 auto' }} />
      <Button color="inherit" onClick={handleNext}>
        {nextText}<KeyboardArrowRight/>
      </Button>
    </Box>
  </>);
}
export default PaymentCard;